import "./AddDocumentsModal.scss"
import uploadImg from "assets/img/upload.svg"
import uploadIcon from "assets/img/icon-upload.svg"
import noDocumentsImg from "assets/img/no-documents.svg"

import React, { FunctionComponent, useState, useRef, useEffect, useMemo } from "react"
import { useTranslation, Trans } from "react-i18next"
import { Link } from "react-router-dom"
import PQueue from "p-queue"
import cx from "classnames"

import { Flex } from "components/Flex"
import Text from "components/Text/Text"
import Modal from "components/Modal/Modal"
import Button from "components/Button/Button"
import { DOCUMENT_UPLOAD_MAX_SIZE_MB, DOCUMENT_SUPPORTED_TYPES_LABEL, DOCUMENT_INPUT_FILE_ACCEPT, DOCUMENT_SUPPORTED_EXTENSIONS, DOCUMENT_SUPPORTED_TYPES } from "config/constants"
import CustomError from "models/CustomError"
import DropFiles from "components/DropFiles/DropFiles"
import FormField from "components/FormField/FormField"
import Select, { ISelectOption } from "components/Select/Select"
import ValidationMessage from "components/ValidationMessage/ValidationMessage"
import { Guid } from "@/types"
import Form from "components/Form/Form"
import AddDocumentsDocument from "./AddDocumentsDocument"
import ValidationService from "services/ValidationService"
import toastMessages from "stores/toastMessages"
import Routes from "@/routes"
import fileUtils from "utils/file"
import DroppableArea from "components/DroppableArea/DroppableArea"


interface IAddDocumentsModalProps {
    categories?: ISelectOption<Guid>[]
    categoryRequired?: boolean
    onSave(data: { documentCategoryId?: Guid, files: File[] }): Promise<void>
    onClose(): void
}

export const THUMBNAILS_MAX_COUNT = 8

const AddDocumentsModal: FunctionComponent<React.PropsWithChildren<IAddDocumentsModalProps>> = ({
    categories,
    categoryRequired,
    onSave,
    onClose
}) => {
    const { t } = useTranslation()
    const [isSubmitted, setIsSubmitted] = useState(false)
    const [isUploading, setIsUploading] = useState(false)
    const [error, setError] = useState("")
    const [selectedFiles, setSelectedFiles] = useState<File[]>([])
    const inputFileEl = useRef<HTMLInputElement>(null)
    const [isLoadingFiles, setIsLoadingFiles] = useState(false)
    const [documentCategoryId, setDocumentCategoryId] = useState<Guid | null>(null)

    const documentCategoryValidationError = useMemo((): CustomError | null => {
        return isSubmitted ? ValidationService.validate({ required: true }, documentCategoryId) : null
    }, [documentCategoryId, isSubmitted])

    useEffect(() => {
        // preselect category if there's only one
        if (categories?.length === 1) {
            setDocumentCategoryId(categories[0].value)
        }
    }, [categories])

    function handleDocumentCategorySelect(option?: ISelectOption) {
        setDocumentCategoryId(option?.value || null)
    }

    function handleInputFileChange(event: React.ChangeEvent<HTMLInputElement>) {
        const files = event.target.files ? Array.from(event.target.files) : null
        event.target.value = ""
        if (!files || !files.length) return
        handleFilesSelect(files)
    }

    async function handleFilesSelect(files: File[]) {
        setError("")
        setIsLoadingFiles(true)

        const validFiles: File[] = []
        const queue = new PQueue({ concurrency: 1 })

        const validateFile = async (file: File) => {
            try {
                await fileUtils.validateDocumentFile({
                    file,
                    extensions: DOCUMENT_SUPPORTED_EXTENSIONS,
                    types: DOCUMENT_SUPPORTED_TYPES,
                    maxSizeMb: DOCUMENT_UPLOAD_MAX_SIZE_MB,
                })
                validFiles.push(file)
            } catch (e) {
                if (e instanceof CustomError) {
                    setError(e.message)
                } else {
                    setError(t("validation.failedToLoadImage"))
                }
            }
        }

        for (const file of files) {
            queue.add(validateFile.bind(null, file))
        }

        await queue.onIdle()

        setSelectedFiles([...selectedFiles, ...validFiles])
        setIsLoadingFiles(false)
    }

    async function handleSave() {
        setIsSubmitted(true)

        if ((categoryRequired
            ? (documentCategoryValidationError || !documentCategoryId)
            : false
        ) || selectedFiles.length === 0) return

        setIsUploading(true)
        try {
            await onSave({
                documentCategoryId: documentCategoryId ?? undefined,
                files: selectedFiles
            })
        } catch (e) {
            setIsUploading(false)
            toastMessages.showSystemError()
            return
        }
        onClose()
    }

    function handleDeleteDocument(file: File) {
        setSelectedFiles(selectedFiles.filter(sf => sf !== file))
    }

    const chooseDocumentButton = (
        <Button
            label={t("addDocuments.chooseDocument")}
            onClick={() => inputFileEl.current?.click()}
            loading={isLoadingFiles}
            disabled={isLoadingFiles || isUploading} />
    )

    return (
        <Modal
            title={t("addDocuments.title")}
            onClose={isUploading ? undefined : onClose}
            maxWidth="1000px"
            footer={!categoryRequired || categories?.length ?
                <Flex justifyContent="flex-end">
                    <Button
                        theme="secondary"
                        label={t("common.cancel")}
                        onClick={onClose}
                        disabled={isUploading} />
                    <Button
                        label={t("common.save")}
                        onClick={handleSave}
                        className="ml-2"
                        disabled={isUploading || selectedFiles.length === 0}
                        loading={isUploading} />
                </Flex>
                : null
            }>

            {(!categoryRequired || (!!categories && categories.length > 0)) &&
                <>
                    {categoryRequired && !!categories && categories.length > 0 &&
                        <Form onSubmit={handleSave} className="mb-3">
                            <FormField
                                label={t("addDocuments.selectDocumentCategory")}>
                                <Select
                                    value={documentCategoryId}
                                    options={categories}
                                    onChange={handleDocumentCategorySelect}
                                    isClearable
                                    isInModal
                                    isInvalid={!!documentCategoryValidationError}
                                />
                                {documentCategoryValidationError &&
                                    <ValidationMessage>{documentCategoryValidationError.message}</ValidationMessage>
                                }
                            </FormField>
                        </Form>
                    }

                    <DropFiles onDrop={handleFilesSelect} enabled={!isUploading && !isLoadingFiles}>
                        {({ isDragOver }) => (
                            <DroppableArea isDragOver={isDragOver}>
                                <div className={cx(["add-documents-modal__drop", {
                                    "add-documents-modal__drop--small": selectedFiles.length > 0
                                }])}>

                                    {selectedFiles.length === 0 &&
                                        <>
                                            <img src={uploadImg} alt="" />
                                            <Text className="mb-4" preLine>{t("addDocuments.chooseDocumentMaxX", { max: DOCUMENT_UPLOAD_MAX_SIZE_MB, formats: DOCUMENT_SUPPORTED_TYPES_LABEL })}</Text>
                                            {chooseDocumentButton}
                                        </>
                                    }

                                    {selectedFiles.length > 0 &&
                                        <>
                                            <Flex className="mb-2" alignItems="center">
                                                <img src={uploadIcon} className="mr-1" alt="" />
                                                {t("addDocuments.dropDocumentsHere")} ({DOCUMENT_SUPPORTED_TYPES_LABEL})
                                            </Flex>
                                            {chooseDocumentButton}
                                        </>
                                    }

                                    <input
                                        type="file"
                                        multiple
                                        accept={DOCUMENT_INPUT_FILE_ACCEPT.join(",")}
                                        className="add-documents-modal__input-file"
                                        onChange={handleInputFileChange}
                                        ref={inputFileEl}
                                        data-testid="add-documents-input-file" />

                                    {!!error &&
                                        <div className={selectedFiles.length > 0 ? "mt-2" : "mt-3"}>
                                            <ValidationMessage>{error}</ValidationMessage>
                                        </div>
                                    }
                                </div>
                            </DroppableArea>
                        )}
                    </DropFiles>

                    {selectedFiles.length > 0 &&
                        <div className="mt-3">
                            {selectedFiles.map((file, idx) => (
                                <AddDocumentsDocument
                                    key={idx}
                                    file={file}
                                    onDelete={() => handleDeleteDocument(file)}
                                    isDisabled={isUploading}
                                />
                            ))}
                        </div>
                    }
                </>
            }

            {categoryRequired && (!categories || categories.length === 0) &&
                <div className="text-center pt-2 pb-4">
                    <img src={noDocumentsImg} alt="" />
                    <div className="color-grey-500 white-space-pre-line mt-2">
                        <Trans i18nKey="addDocuments.noDocumentCategories">
                            No document categories defined yet.
                            To add documents a document category needs to be defined first.
                            Document categories are defined in the <Link to={Routes.tenantSettings.getPath()} className="link color-brand-blue">tenant settings</Link>.
                        </Trans>
                    </div>
                </div>
            }

        </Modal>
    )
}

export default AddDocumentsModal
